/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Card, Grid, Typography2, VisuallyHidden } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import LearnerObjectSettingItems from 'bundles/personalization-settings/components/LearnerObjectSettingItems';
import { getLearnerObjectSettings } from 'bundles/personalization-settings/constants';
import { PERSONALIZATION_SETTINGS_FORM } from 'bundles/personalization-settings/hooks/usePersonalizationEventTracking';
import usePersonalizationSettingsAttributes from 'bundles/personalization-settings/hooks/usePersonalizationSettingsAttributes';

import _t from 'i18n!nls/personalization-settings';

// CDS Card has no padding prop -- its default inner padding (--cds-spacing-100,
// 8px) is a general-purpose default, not this page's Figma spec (16px). Override
// via CDS's own public class hook rather than forking the shared Card component.
// Card's own rule is `&.cds-Card-base .cds-Card-innerCard` (3 classes deep), so a
// plain `.cds-Card-innerCard` override loses on specificity regardless of source
// order -- `!important` is the only way to win against a shared component's own
// internal, non-overridable style. Scoped via `&` so it only applies to this Card.
const styles = {
  card: css`
    & .cds-Card-innerCard {
      padding: var(--cds-spacing-200) !important;
      min-width: 0;
    }
  `,
};

/**
 * The "Personalization" account-settings tab content: a CDS card listing the
 * learner's explicit Learner Object attributes.
 *
 * Loads the learner's explicit attributes on mount (LO-4743) and binds them to
 * the field rows. Per-field editors + Save land in follow-up tickets (the Save
 * action is disabled until then). The component is dark in production until the
 * mounting bundle gates its registration on `PERSONALIZATION_SETTINGS_GATE`.
 */
const PersonalizationTab = () => {
  const { values, loading } = usePersonalizationSettingsAttributes();

  // Fired once, the first time the card becomes visible.
  const viewFormTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_form', {
    form: {
      ...PERSONALIZATION_SETTINGS_FORM,
      currentStep: 1,
      totalSteps: 1,
      fieldsAvailable: getLearnerObjectSettings().length,
    },
  });

  return (
    <div ref={viewFormTrackingRef}>
      <Card css={styles.card} interactive={false} data-testid="personalization-tab">
        <Grid container direction="column" spacing={16}>
          <Grid item>
            <Typography2 component="h2" variant="titleSmall">
              {_t('Personalization settings')}
            </Typography2>
          </Grid>
          <Grid item zeroMinWidth>
            {/* Always present in the DOM so assistive tech can detect status updates
                (loading now, and the Save confirmation that lands in LO-4744). */}
            <VisuallyHidden role="status" aria-live="polite">
              {loading ? _t('Loading personalization settings…') : null}
            </VisuallyHidden>
            <LearnerObjectSettingItems loading={loading} values={values} />
          </Grid>
        </Grid>
      </Card>
    </div>
  );
};

export default PersonalizationTab;
